import { Application, Graphics, Text, TextStyle, Container, Sprite, Assets, Texture } from 'pixi.js';
import { apiClient, GameObject, RoundData, GunType } from '../api/client.ts';
import { soundManager } from './SoundManager.ts';
import { wsClient, PlayerInfo } from '../multiplayer/WebSocketClient.ts';
import { RemotePlayerManager } from '../multiplayer/RemotePlayer.ts';
import { MenuController } from '../ui/MenuController.ts';
import { TuningShopController } from '../ui/TuningShopController.ts';

type GameState = 'demo' | 'idle' | 'taking_off' | 'flying' | 'finished' | 'spectating';
type GameMode = 'solo' | 'multiplayer' | 'training';

interface PlaneState {
  x: number;
  y: number;
  vx: number;
  vy: number;
  alive: boolean;
}

interface GameObjectSprite {
  data: GameObject;
  container: Container;
  collected: boolean;
  index: number;
  missileHits?: number; // Для подсчёта попаданий в ракету
}

interface Bullet {
  id: string;
  x: number;
  y: number;
  vx: number;
  vy: number;
  sprite: Graphics;
  shooterId: number;
  distance: number; // Пройденная дистанция
}

interface TuningState {
  gun: GunType | null;
  ammo: number;
  armor: number;
  boost: number;
}

interface EnemyState {
  obj: GameObjectSprite;
  currentY: number;
  vy: number;
  lastShootTime: number;
  nextDirectionChange: number;
}

interface TurretState {
  obj: GameObjectSprite;
  lastShootTime: number;
}

interface EnemyBullet {
  x: number;
  y: number;
  vx: number;
  vy: number;
  sprite: Graphics;
}

export class Game {
  private app: Application;
  private canvas: HTMLCanvasElement;

  // Game state
  private state: GameState = 'demo';
  private roundData: RoundData | null = null;
  private plane: PlaneState = { x: 80, y: 0, vx: 0, vy: 0, alive: true };
  private collectedIndices: number[] = [];
  private collectedBonuses: number = 0;
  private demoTime: number = 0;

  // Earnings rate (same as backend)
  private readonly DISTANCE_RATE: number = 0.002;

  // Graphics
  private planeSprite!: Sprite;
  private carrierGraphics!: Graphics;
  private objectsContainer!: Container;
  private gameObjects: GameObjectSprite[] = [];
  private effectsContainer!: Container;
  private cloudsContainer!: Container;
  private wavesGraphics!: Graphics;
  private waveOffset: number = 0;
  private lastCameraX: number = 0;

  // Textures
  private textures: {
    bonus: any;
    multiplier: any;
    missile: any;
    cloud: any;
    fuel_canister: any;
    turret: any;
    enemy_plane: any;
  } | null = null;

  // UI references
  private distanceEl!: HTMLElement;
  private balanceEl!: HTMLElement;
  private messageEl!: HTMLElement;
  private startBtn!: HTMLButtonElement;
  private flapBtn!: HTMLButtonElement;
  private fuelBar!: HTMLElement;
  private fuelText!: HTMLElement;
  private statsTable!: HTMLElement;
  private gunRow!: HTMLElement;
  private landingDialog!: HTMLElement;

  // Game values
  private balance: number = 0;
  private currentMultiplier: number = 1.0;
  private currentDistance: number = 0;

  // Field dimensions
  private fieldHeight: number = 400;
  private carrierWidth: number = 200;
  private carrierY: number = 0;

  // Camera
  private cameraX: number = 0;

  // Flight physics
  private gravity: number = 200;
  private flapStrength: number = -180;
  private maxFallSpeed: number = 250;
  private maxRiseSpeed: number = -200;

  // Multiplayer
  private gameMode: GameMode = 'solo';
  private menuController!: MenuController;
  private remotePlayerManager: RemotePlayerManager | null = null;
  private mpObjectsMap: any[] = [];
  private myUserId: number = 0;
  private positionSendInterval: number = 50; // ms
  private lastPositionSendTime: number = 0;
  private planeTexture: Texture | null = null;

  // Fuel system
  private fuel: number = 100;
  private maxFuel: number = 100;
  private fuelConsumption: number = 8; // Per second
  private refuelCost: number = 5; // FLY per 10% fuel
  private refuelAmount: number = 10; // 10% per refuel
  private landingPadInterval: number = 1500; // Distance between pads
  private currentSegment: number = 0; // Which segment we're on
  private refueledOnPad: boolean = false; // Did player refuel on current pad
  private missedLandings: number = 0; // Training: count of pads flown over without landing
  private successfulLandings: number = 0; // Training: count of successful landings
  private landedPads: Set<number> = new Set(); // Training: pads we've landed on
  private lastCheckedPadSegment: number = 0; // Last pad segment we checked for missing

  // Multiplayer finish line (5000m per player)
  private mpFinishDistance: number = 10000;

  // Training finish line
  private readonly trainingFinishDistance: number = 20000;

  // Tuning system
  private tuning: TuningState = { gun: null, ammo: 0, armor: 0, boost: 0 };
  private initialAmmo: number = 0;  // Track initial ammo for reporting to server
  private initialArmor: number = 0;  // Track initial armor for reporting to server
  private initialBoost: number = 0;  // Track initial boost for reporting to server
  private readonly BOOST_DISTANCE: number = 100;  // Рывок вперёд на 100px
  private bullets: Bullet[] = [];
  private bulletsContainer!: Container;
  private lastShootTime: number = 0;
  private readonly BULLET_SPEED: number = 600;
  private readonly BULLET_MAX_DISTANCE: number = 400;
  private readonly HITS_TO_KILL_MISSILE: number = 3;
  // HITS_TO_KILL_PLAYER is handled server-side in multiplayer
  private bulletIdCounter: number = 0;

  // Enemy system
  private enemyPlanes: EnemyState[] = [];
  private turrets: TurretState[] = [];
  private enemyBullets: EnemyBullet[] = [];
  private enemyBulletsContainer!: Container;
  private readonly ENEMY_BULLET_SPEED: number = 300;
  private readonly TURRET_BULLET_SPEED: number = 250;

  constructor(canvas: HTMLCanvasElement) {
    this.canvas = canvas;
    this.app = new Application();
  }

  async init() {
    // Wait for layout to settle
    await new Promise(r => setTimeout(r, 100));

    // Get dimensions - try multiple methods for mobile compatibility
    const rect = this.canvas.getBoundingClientRect();
    const uiPanel = document.getElementById('ui-panel');
    const uiHeight = uiPanel?.offsetHeight || 150;

    let width = rect.width || window.innerWidth;
    let height = rect.height || (window.innerHeight - uiHeight);

    // Ensure minimum dimensions
    width = Math.max(300, width);
    height = Math.max(150, height);

    console.log('Canvas size:', width, height, 'rect:', rect.width, rect.height); // Debug

    await this.app.init({
      canvas: this.canvas,
      width,
      height,
      backgroundColor: 0x0a1628,
      antialias: true,
      resolution: window.devicePixelRatio || 1,
      autoDensity: true
    });

    // Adjust carrier position based on available height
    this.carrierY = height - Math.min(80, height * 0.15);
    this.plane.y = this.carrierY - 30;

    // Handle resize
    window.addEventListener('resize', () => this.handleResize());

    await this.createGraphics();
    this.bindUI();
    this.app.ticker.add(this.update.bind(this));

    // Initialize menu controller
    this.menuController = new MenuController({
      onStartSoloGame: () => this.startSoloGame(),
      onStartTrainingGame: () => this.startTrainingGame(),
      onStartMultiplayerGame: (roomId, seed, objectsMap, players, startAt) =>
        this.startMultiplayerGame(roomId, seed, objectsMap, players, startAt),
      onShowLeaderboard: () => this.showLeaderboard()
    });

    // Initialize tuning shop controller (instance manages itself via DOM events)
    new TuningShopController({
      onPurchase: async () => {
        await this.refreshTuning();
        // Also update menu balance
        const profile = await apiClient.getProfile();
        this.balance = profile.balance;
        this.menuController.updateBalance(profile.balance);
        this.updateUI();
      }
    });

    // Setup multiplayer event handlers
    this.setupMultiplayerHandlers();

    // Initialize user
    await this.initUser();
  }

  private async initUser() {
    // Setup auth UI handlers
    this.setupAuth();
    this.setupLandingPage();

    // Check if already authenticated
    const authData = await apiClient.checkAuth();
    if (authData) {
      this.hideLandingPage();
      this.onAuthSuccess(authData.balance, authData.nickname);
    } else if (apiClient.isAuthenticated()) {
      // Have token but server unavailable - use cached data
      this.hideLandingPage();
      const nickname = apiClient.getNickname() || 'Player';
      this.onAuthSuccess(0, nickname); // Balance will sync when server is available
    } else {
      // Not authenticated - show landing page (auth overlay is hidden by default now)
      this.showLandingPage();
    }
  }

  private setupLandingPage() {
    const playBtn = document.getElementById('landing-play-btn');
    if (playBtn) {
      playBtn.addEventListener('click', () => {
        this.hideLandingPage();
        this.showAuthOverlay();
      });
    }
  }

  private showLandingPage() {
    document.getElementById('landing-page')?.classList.remove('hidden');
  }

  private hideLandingPage() {
    document.getElementById('landing-page')?.classList.add('hidden');
  }

  private setupAuth() {
    const tabLogin = document.getElementById('tab-login')!;
    const tabRegister = document.getElementById('tab-register')!;
    const formLogin = document.getElementById('form-login')!;
    const formRegister = document.getElementById('form-register')!;
    const authError = document.getElementById('auth-error')!;
    const userNickname = document.getElementById('user-nickname')!;

    // Tab switching
    tabLogin.addEventListener('click', () => {
      tabLogin.classList.add('active');
      tabRegister.classList.remove('active');
      formLogin.classList.add('active');
      formRegister.classList.remove('active');
      authError.textContent = '';
    });

    tabRegister.addEventListener('click', () => {
      tabRegister.classList.add('active');
      tabLogin.classList.remove('active');
      formRegister.classList.add('active');
      formLogin.classList.remove('active');
      authError.textContent = '';
    });

    // Login form
    formLogin.addEventListener('submit', async (e) => {
      e.preventDefault();
      const email = (document.getElementById('login-email') as HTMLInputElement).value;
      const password = (document.getElementById('login-password') as HTMLInputElement).value;
      const submitBtn = document.getElementById('login-submit') as HTMLButtonElement;

      submitBtn.disabled = true;
      authError.textContent = '';

      try {
        const data = await apiClient.login(email, password);
        this.onAuthSuccess(data.balance, data.nickname);
      } catch (err: any) {
        authError.textContent = err.message || 'Ошибка входа';
      } finally {
        submitBtn.disabled = false;
      }
    });

    // Register form
    formRegister.addEventListener('submit', async (e) => {
      e.preventDefault();
      const email = (document.getElementById('register-email') as HTMLInputElement).value;
      const nickname = (document.getElementById('register-nickname') as HTMLInputElement).value;
      const password = (document.getElementById('register-password') as HTMLInputElement).value;
      const submitBtn = document.getElementById('register-submit') as HTMLButtonElement;

      submitBtn.disabled = true;
      authError.textContent = '';

      try {
        const data = await apiClient.register(email, password, nickname);
        this.onAuthSuccess(data.balance, data.nickname);
      } catch (err: any) {
        authError.textContent = err.message || 'Ошибка регистрации';
      } finally {
        submitBtn.disabled = false;
      }
    });

    // Open profile on nickname click
    userNickname.addEventListener('click', () => {
      this.openProfile();
    });

    // Profile handlers
    this.setupProfile();
  }

  private setupProfile() {
    const profileOverlay = document.getElementById('profile-overlay')!;
    const profileClose = document.getElementById('profile-close')!;
    const profileLogout = document.getElementById('profile-logout')!;
    const profileNicknameInput = document.getElementById('profile-nickname-input') as HTMLInputElement;
    const profileNicknameSave = document.getElementById('profile-nickname-save')!;
    const profileError = document.getElementById('profile-error')!;

    profileClose.addEventListener('click', () => {
      profileOverlay.classList.add('hidden');
    });

    profileLogout.addEventListener('click', () => {
      apiClient.logout();
      profileOverlay.classList.add('hidden');
      this.showLandingPage();
    });

    profileNicknameSave.addEventListener('click', async () => {
      const newNickname = profileNicknameInput.value.trim();
      if (newNickname.length < 2) {
        profileError.textContent = 'Минимум 2 символа';
        return;
      }

      profileNicknameSave.setAttribute('disabled', 'true');
      profileError.textContent = '';

      try {
        const data = await apiClient.updateProfile(newNickname);
        document.getElementById('user-nickname')!.textContent = data.nickname;
        profileError.textContent = '';
      } catch (err: any) {
        profileError.textContent = err.message || 'Ошибка сохранения';
      } finally {
        profileNicknameSave.removeAttribute('disabled');
      }
    });
  }

  private async openProfile() {
    const profileOverlay = document.getElementById('profile-overlay')!;

    try {
      const profile = await apiClient.getProfile();
      document.getElementById('profile-balance')!.textContent = `${profile.balance.toFixed(2)} FLY`;
      document.getElementById('profile-record')!.textContent = `${Math.round(profile.best_distance)} м`;
      document.getElementById('profile-email')!.textContent = profile.email;
      (document.getElementById('profile-nickname-input') as HTMLInputElement).value = profile.nickname;
      document.getElementById('profile-error')!.textContent = '';
      profileOverlay.classList.remove('hidden');
    } catch {
      // Ignore errors
    }
  }

  private showAuthOverlay() {
    document.getElementById('auth-overlay')!.classList.remove('hidden');
    document.getElementById('user-nickname')!.textContent = '';
  }

  private async onAuthSuccess(balance: number, nickname: string) {
    this.balance = balance;
    this.myUserId = apiClient.getUserId() || 0;
    document.getElementById('user-nickname')!.textContent = nickname;
    document.getElementById('auth-overlay')!.classList.add('hidden');

    // Stop demo and reset to idle
    this.state = 'idle';
    this.plane.x = 80;
    this.plane.y = this.carrierY - 30;
    this.plane.vx = 0;
    this.plane.vy = 0;
    this.cameraX = 0;
    this.lastCameraX = 0;
    // Load tuning data
    await this.loadTuning();

    this.updateUI();
    this.updateTuningUI();

    // Load user settings and show main menu
    this.menuController.loadSettings();
    this.menuController.updateBalance(balance);
    this.menuController.showMainMenu();
  }

  private async loadTuning(): Promise<void> {
    try {
      const tuning = await apiClient.getMyTuning();
      this.tuning = {
        gun: tuning.gun,
        ammo: tuning.ammo,
        armor: tuning.armor,
        boost: tuning.boost
      };
      // Store initial values for reporting spent amounts
      this.initialAmmo = tuning.ammo;
      this.initialArmor = tuning.armor;
      this.initialBoost = tuning.boost;
    } catch {
      // Use defaults
      this.tuning = { gun: null, ammo: 0, armor: 0, boost: 0 };
      this.initialAmmo = 0;
      this.initialArmor = 0;
      this.initialBoost = 0;
    }
  }

  private async createGraphics() {
    // Sky gradient
    const sky = new Graphics();
    sky.rect(0, 0, this.app.screen.width, this.app.screen.height);
    sky.fill(0x1a3a6a);
    this.app.stage.addChild(sky);

    // Clouds container (behind everything)
    this.cloudsContainer = new Container();
    this.app.stage.addChild(this.cloudsContainer);

    // Sea base
    const sea = new Graphics();
    sea.rect(0, this.carrierY + 50, this.app.screen.width, this.app.screen.height - this.carrierY);
    sea.fill(0x0a2848);
    this.app.stage.addChild(sea);

    // Waves graphics (animated)
    this.wavesGraphics = new Graphics();
    this.app.stage.addChild(this.wavesGraphics);

    // Objects container (will scroll)
    this.objectsContainer = new Container();
    this.app.stage.addChild(this.objectsContainer);

    // Effects container
    this.effectsContainer = new Container();
    this.app.stage.addChild(this.effectsContainer);

    // Bullets container
    this.bulletsContainer = new Container();
    this.app.stage.addChild(this.bulletsContainer);

    // Enemy bullets container
    this.enemyBulletsContainer = new Container();
    this.app.stage.addChild(this.enemyBulletsContainer);

    // Carrier
    this.carrierGraphics = new Graphics();
    this.drawCarrier();
    this.app.stage.addChild(this.carrierGraphics);

    // Load all textures
    const [planeTexture, bonusTexture, multiplierTexture, missileTexture, cloudTexture, fuelCanisterTexture, turretTexture, enemyPlaneTexture] = await Promise.all([
      Assets.load('/sprites/plane.svg'),
      Assets.load('/sprites/bonus.svg'),
      Assets.load('/sprites/multiplier.svg'),
      Assets.load('/sprites/missile.svg'),
      Assets.load('/sprites/cloud.svg'),
      Assets.load('/sprites/fuel_canister.svg'),
      Assets.load('/sprites/turret.svg'),
      Assets.load('/sprites/enemy_plane.svg')
    ]);

    this.textures = {
      bonus: bonusTexture,
      multiplier: multiplierTexture,
      missile: missileTexture,
      cloud: cloudTexture,
      fuel_canister: fuelCanisterTexture,
      turret: turretTexture,
      enemy_plane: enemyPlaneTexture
    };

    // Create clouds with sprites
    this.createClouds();

    // Save plane texture for multiplayer
    this.planeTexture = planeTexture;

    // Create plane sprite
    this.planeSprite = new Sprite(planeTexture);
    this.planeSprite.anchor.set(0.5);
    this.planeSprite.scale.set(0.8);
    this.updatePlane();
    this.app.stage.addChild(this.planeSprite);
  }

  private drawCarrier() {
    const g = this.carrierGraphics;
    g.clear();

    // Starting carrier deck
    const startX = -this.cameraX;
    if (startX + this.carrierWidth > 0 && startX < this.app.screen.width) {
      g.rect(startX, this.carrierY, this.carrierWidth, 60);
      g.fill(0x4a5a6a);
      // Tower
      g.rect(startX + 140, this.carrierY - 40, 40, 40);
      g.fill(0x5a6a7a);
    }

    // Landing pads in solo and training mode
    if (this.gameMode === 'solo' || this.gameMode === 'training') {
      const padWidth = 150;
      // Calculate how many pads to draw based on player position (not just currentSegment)
      const playerSegment = Math.floor((this.plane.x - this.carrierWidth) / this.landingPadInterval);
      const maxSegment = Math.max(this.currentSegment + 3, playerSegment + 3);

      for (let seg = 1; seg <= maxSegment; seg++) {
        const padX = this.carrierWidth + seg * this.landingPadInterval;
        const padScreenX = padX - this.cameraX;

        // Only draw if visible on screen
        if (padScreenX + padWidth < 0 || padScreenX > this.app.screen.width) continue;

        // Different color: green for target, dark for future, red for missed
        // In training: missed = pads we flew past (seg <= lastCheckedPadSegment) but didn't land on
        // A pad is "landed on" if currentSegment > seg (we moved past it via landing)
        const landedOn = seg < this.currentSegment;
        const missedInTraining = this.gameMode === 'training' && seg <= this.lastCheckedPadSegment && !landedOn;
        const isMissed = missedInTraining;
        const isTarget = this.gameMode === 'training' ? seg === this.lastCheckedPadSegment + 1 : seg === this.currentSegment;
        const padColor = isMissed ? 0x8a4a4a : (isTarget ? 0x4a8a4a : 0x4a6a4a);

        // Pad base
        g.rect(padScreenX, this.carrierY, padWidth, 60);
        g.fill(padColor);

        // Landing stripes
        g.rect(padScreenX + 20, this.carrierY + 5, 10, 50);
        g.fill(0xffcc00);
        g.rect(padScreenX + 40, this.carrierY + 5, 10, 50);
        g.fill(0xffcc00);
        g.rect(padScreenX + padWidth - 50, this.carrierY + 5, 10, 50);
        g.fill(0xffcc00);
        g.rect(padScreenX + padWidth - 30, this.carrierY + 5, 10, 50);
        g.fill(0xffcc00);

        // Segment number
        g.rect(padScreenX + 65, this.carrierY + 15, 20, 30);
        g.fill(0xffffff);
      }
    }

    // Multiplayer finish line
    if (this.gameMode === 'multiplayer') {
      const finishScreenX = this.mpFinishDistance - this.cameraX;

      // Only draw if visible on screen
      if (finishScreenX > -50 && finishScreenX < this.app.screen.width + 50) {
        // Finish line pole
        g.rect(finishScreenX - 3, 30, 6, this.carrierY - 30);
        g.fill(0xffffff);

        // Checkered flag pattern
        const flagWidth = 60;
        const flagHeight = 40;
        const flagY = 30;
        const squareSize = 10;

        for (let row = 0; row < flagHeight / squareSize; row++) {
          for (let col = 0; col < flagWidth / squareSize; col++) {
            const isWhite = (row + col) % 2 === 0;
            g.rect(
              finishScreenX + col * squareSize,
              flagY + row * squareSize,
              squareSize,
              squareSize
            );
            g.fill(isWhite ? 0xffffff : 0x000000);
          }
        }

        // "FINISH" text indicator on ground
        g.rect(finishScreenX - 40, this.carrierY, 80, 10);
        g.fill(0xffcc00);
      }
    }

    // Training finish line (20km)
    if (this.gameMode === 'training') {
      const finishScreenX = this.trainingFinishDistance - this.cameraX;

      // Only draw if visible on screen
      if (finishScreenX > -50 && finishScreenX < this.app.screen.width + 50) {
        // Finish line pole
        g.rect(finishScreenX - 3, 30, 6, this.carrierY - 30);
        g.fill(0x00ff00);

        // Checkered flag pattern (green/white for training)
        const flagWidth = 60;
        const flagHeight = 40;
        const flagY = 30;
        const squareSize = 10;

        for (let row = 0; row < flagHeight / squareSize; row++) {
          for (let col = 0; col < flagWidth / squareSize; col++) {
            const isWhite = (row + col) % 2 === 0;
            g.rect(
              finishScreenX + col * squareSize,
              flagY + row * squareSize,
              squareSize,
              squareSize
            );
            g.fill(isWhite ? 0xffffff : 0x00cc00);
          }
        }

        // "20 KM" indicator on ground
        g.rect(finishScreenX - 40, this.carrierY, 80, 10);
        g.fill(0x00ff00);
      }
    }
  }

  private createClouds() {
    if (!this.textures) return;

    // Create several clouds at random positions
    const cloudCount = 10;
    for (let i = 0; i < cloudCount; i++) {
      const cloud = new Sprite(this.textures.cloud);
      cloud.anchor.set(0.5);

      const x = Math.random() * this.app.screen.width * 3;
      const y = 30 + Math.random() * (this.carrierY * 0.5);
      const scale = 0.4 + Math.random() * 0.6;

      cloud.x = x;
      cloud.y = y;
      cloud.scale.set(scale);
      cloud.alpha = 0.4 + Math.random() * 0.3;

      this.cloudsContainer.addChild(cloud);
    }
  }

  private drawWaves() {
    const g = this.wavesGraphics;
    g.clear();

    const waveY = this.carrierY + 48;
    const amplitude = 3;
    const wavelength = 40;

    // Draw multiple wave layers
    for (let layer = 0; layer < 3; layer++) {
      const layerOffset = layer * 15;
      const layerAlpha = 0.3 - layer * 0.08;
      const layerY = waveY + layer * 4;

      g.moveTo(-wavelength, layerY);
      for (let x = -wavelength; x <= this.app.screen.width + wavelength; x += 5) {
        const y = layerY + Math.sin((x + this.waveOffset + layerOffset) / wavelength * Math.PI * 2) * amplitude;
        g.lineTo(x, y);
      }
      g.lineTo(this.app.screen.width + wavelength, this.app.screen.height);
      g.lineTo(-wavelength, this.app.screen.height);
      g.closePath();
      g.fill({ color: 0x3a6a9a, alpha: layerAlpha });
    }
  }

  private updateClouds() {
    // Parallax: clouds scroll with camera but slower than world objects
    // World objects: screenX = worldX - cameraX (100% camera speed)
    // Clouds: move at ~50% of camera speed for parallax effect
    const cameraDelta = this.cameraX - this.lastCameraX;
    const parallaxFactor = 0.5;
    const driftSpeed = 0.3; // Small constant drift

    for (const cloud of this.cloudsContainer.children) {
      // Move with camera (but slower) + constant drift
      cloud.x -= cameraDelta * parallaxFactor + driftSpeed;

      // Wrap around when off screen
      if (cloud.x < -100) {
        cloud.x = this.app.screen.width + 100 + Math.random() * 100;
        cloud.y = 30 + Math.random() * (this.carrierY * 0.5);
      }
    }

    this.lastCameraX = this.cameraX;
  }

  private updatePlane() {
    if (!this.planeSprite) return;

    this.planeSprite.visible = this.plane.alive;
    if (!this.plane.alive) return;

    const screenX = this.plane.x - this.cameraX;
    const screenY = this.plane.y;

    this.planeSprite.x = screenX;
    this.planeSprite.y = screenY;

    // Slight rotation based on vertical velocity
    this.planeSprite.rotation = this.plane.vy * 0.002;
  }

  private bindUI() {
    this.distanceEl = document.getElementById('distance-value')!;
    this.balanceEl = document.getElementById('balance-value')!;
    this.messageEl = document.getElementById('message')!;
    this.startBtn = document.getElementById('start-btn') as HTMLButtonElement;
    this.startBtn.style.display = 'none'; // Hidden - game starts from menu

    this.flapBtn = document.getElementById('flap-btn') as HTMLButtonElement;
    this.fuelBar = document.getElementById('fuel-bar')!;
    this.fuelText = document.getElementById('fuel-text')!;
    this.statsTable = document.getElementById('stats-table')!;
    this.gunRow = document.getElementById('gun-row')!;
    this.landingDialog = document.getElementById('landing-dialog')!;

    // Landing dialog buttons
    document.getElementById('btn-refuel')!.addEventListener('click', () => this.onRefuel());
    document.getElementById('btn-cashout')!.addEventListener('click', () => this.onCashout());
    document.getElementById('btn-continue')!.addEventListener('click', () => this.onContinueFlight());

    this.startBtn.addEventListener('click', () => {
      soundManager.init(); // Initialize audio on user interaction
      this.startGame();
    });

    // Flap button for mobile
    this.flapBtn.addEventListener('touchstart', (e) => {
      e.preventDefault();
      if (this.state === 'flying') {
        this.flap();
      }
    });
    this.flapBtn.addEventListener('click', () => {
      if (this.state === 'flying') {
        this.flap();
      }
    });

    // Shoot button
    const shootBtn = document.getElementById('shoot-btn');
    if (shootBtn) {
      shootBtn.addEventListener('touchstart', (e) => {
        e.preventDefault();
        if (this.state === 'flying') {
          this.shoot();
        }
      });
      shootBtn.addEventListener('click', () => {
        if (this.state === 'flying') {
          this.shoot();
        }
      });
    }

    // Spacebar to start game or flap, Right Shift to shoot
    document.addEventListener('keydown', (e) => {
      if (e.code === 'Space') {
        e.preventDefault();
        if (this.state === 'idle') {
          soundManager.init();
          this.startGame();
        } else if (this.state === 'flying') {
          this.flap();
        }
      } else if (e.code === 'ShiftRight' && this.state === 'flying') {
        e.preventDefault();
        this.shoot();
      } else if (e.code === 'ShiftLeft' && this.state === 'flying') {
        e.preventDefault();
        this.useBoost();
      }
    });

    // Touch to flap (mobile)
    this.canvas.addEventListener('touchstart', (e) => {
      e.preventDefault();
      if (this.state === 'idle') {
        soundManager.init();
        this.startGame();
      } else if (this.state === 'flying') {
        this.flap();
      }
    });

    this.updateUI();
  }

  private updateUI() {
    this.distanceEl.textContent = `${this.currentDistance.toFixed(0)}m`;
    this.balanceEl.textContent = `${this.balance.toFixed(2)} FLY`;
  }

  private showMessage(msg: string) {
    this.messageEl.textContent = msg;
  }

  private async startGame() {
    if (this.state !== 'idle') return;

    try {
      // Training mode: set up special tuning without server
      if (this.gameMode === 'training') {
        this.tuning = {
          gun: {
            id: 0,
            name: 'Training Gun',
            price: 0,
            magazine_size: 999,
            fire_mode: 'burst_15',
            burst_count: 15,
            fire_rate: 0.2
          },
          ammo: 999,
          armor: 10,
          boost: 99
        };
        this.initialAmmo = 999;
        this.initialArmor = 10;
      } else {
        // Reload tuning to get fresh values from server (and set initial values for tracking)
        await this.loadTuning();
      }

      // Training mode generates objects locally, no server round
      if (this.gameMode === 'training') {
        this.roundData = this.generateTrainingRound();
      } else {
        this.roundData = await apiClient.startRound();
      }
      this.updateUI();

      // Setup game objects
      this.setupGameObjects();

      // Start takeoff
      this.state = 'taking_off';
      this.plane.x = 80;
      this.plane.y = this.carrierY - 30;
      this.plane.vx = 0;
      this.plane.vy = 0;
      this.plane.alive = true;
      this.collectedIndices = [];
      this.currentMultiplier = 1.0;
      this.currentDistance = 0;
      this.cameraX = 0;
      this.lastCameraX = 0;

      // Initialize fuel
      this.fuel = this.maxFuel;
      this.currentSegment = 1;
      this.missedLandings = 0;
      this.successfulLandings = 0;
      this.landedPads.clear();
      this.lastCheckedPadSegment = 0;
      this.updateFuelUI();

      soundManager.startEngine();
      this.flapBtn.classList.add('visible');
      this.statsTable.classList.add('visible');
      this.updateTuningUI();
      this.updateShootButtonVisibility();
    } catch (e: any) {
      this.showMessage(e.message || 'Ошибка запуска');
      // Return to menu on error
      setTimeout(() => {
        this.menuController.updateBalance(this.balance);
        this.menuController.showMainMenu();
      }, 2000);
    }
  }

  private generateTrainingRound(): RoundData {
    // Generate objects for 20000m training run (same density as normal game)
    const objects: GameObject[] = [];
    const fieldHeight = 400;
    const segmentLength = 1500;
    const carrierWidth = 200;
    const numSegments = Math.ceil(this.trainingFinishDistance / segmentLength);

    // Multiplier values like in normal game
    const multiplierValues = [1.5, 2.0, 2.0, 3.0, 5.0];

    for (let seg = 0; seg < numSegments; seg++) {
      const segStart = carrierWidth + seg * segmentLength + 100;
      const segEnd = carrierWidth + (seg + 1) * segmentLength - 200;

      // 10 bonuses per segment
      for (let i = 0; i < 10; i++) {
        const x = segStart + Math.random() * (segEnd - segStart);
        const y = 50 + Math.random() * (fieldHeight - 100);
        // Values 0.2-2.0 FLY, increasing with segment
        const baseValue = 0.2 + Math.random() * 1.8;
        const value = Math.round(baseValue * (1 + seg * 0.2) * 100) / 100;
        objects.push({ type: 'bonus', x, y, value });
      }

      // 3 multipliers per segment
      for (let i = 0; i < 3; i++) {
        const x = segStart + Math.random() * (segEnd - segStart);
        const y = 80 + Math.random() * (fieldHeight - 160);
        const values = [...multiplierValues];
        if (seg > 2) values.push(5.0);
        if (seg > 5) values.push(10.0);
        const value = values[Math.floor(Math.random() * values.length)];
        objects.push({ type: 'multiplier', x, y, value });
      }

      // 6+ missiles per segment (more in later segments)
      const numMissiles = Math.floor(6 * (1 + seg * 0.15));
      for (let i = 0; i < numMissiles; i++) {
        const x = segStart + 100 + Math.random() * (segEnd - segStart - 100);
        const y = 30 + Math.random() * (fieldHeight - 60);
        objects.push({ type: 'missile', x, y, value: 0 });
      }

      // Extra top missiles
      const topMissiles = 2 + Math.floor(seg / 3);
      for (let i = 0; i < topMissiles; i++) {
        const x = segStart + 100 + Math.random() * (segEnd - segStart - 100);
        const y = fieldHeight - 50 + Math.random() * 40;
        objects.push({ type: 'missile', x, y, value: 0 });
      }

      // Fuel canisters (1 per segment)
      const fuelX = segStart + Math.random() * (segEnd - segStart);
      const fuelY = 100 + Math.random() * (fieldHeight - 200);
      objects.push({ type: 'fuel_canister', x: fuelX, y: fuelY, value: 10 });

      // Turrets (starting from segment 2)
      if (seg >= 2) {
        const numTurrets = seg >= 5 ? 2 : 1;
        for (let i = 0; i < numTurrets; i++) {
          const x = segStart + 200 + Math.random() * (segEnd - segStart - 400);
          const angle = 45 + Math.random() * 30;
          const fire_rate = 2 + Math.random() * 1.5;
          objects.push({ type: 'turret', x, y: 20, value: 0, angle, fire_rate });
        }
      }

      // Enemy planes (starting from segment 3)
      if (seg >= 3) {
        const numEnemies = seg >= 6 ? 2 : 1;
        for (let i = 0; i < numEnemies; i++) {
          const x = segStart + 300 + Math.random() * (segEnd - segStart - 400);
          const y = 150 + Math.random() * (fieldHeight - 230);
          const direction = Math.random() > 0.5 ? 1 : -1;
          const speed = 80 + Math.random() * 40;
          const fire_rate = 1.5 + Math.random();
          objects.push({ type: 'enemy_plane', x, y, value: 0, direction, speed, fire_rate });
        }
      }
    }

    // Sort by x position
    objects.sort((a, b) => a.x - b.x);

    return {
      id: 0,
      seed: 'training',
      objects_map: objects,
      status: 'in_progress'
    };
  }

  private setupGameObjects() {
    // Clear previous
    this.objectsContainer.removeChildren();
    this.gameObjects = [];
    this.enemyPlanes = [];
    this.turrets = [];
    this.clearEnemyBullets();

    if (!this.roundData || !this.textures) return;

    // Fixed scale - field scrolls horizontally
    const scaleY = (this.carrierY - 50) / this.fieldHeight;

    this.roundData.objects_map.forEach((obj, index) => {
      const container = new Container();
      const x = obj.x; // World coordinate
      // Turrets stand on water level, other objects use normal Y scaling
      const y = obj.type === 'turret'
        ? this.carrierY + 50  // Submerged in water, only top visible
        : 50 + (this.fieldHeight - obj.y) * scaleY;

      let sprite: Sprite;

      if (obj.type === 'bonus') {
        sprite = new Sprite(this.textures!.bonus);
        sprite.anchor.set(0.5);
        sprite.scale.set(0.75);
        container.addChild(sprite);

        // Value label
        const text = new Text({
          text: `+${obj.value.toFixed(0)}`,
          style: new TextStyle({ fontSize: 11, fill: 0xffffff, fontWeight: 'bold' })
        });
        text.anchor.set(0.5);
        text.y = 24;
        container.addChild(text);

      } else if (obj.type === 'multiplier') {
        sprite = new Sprite(this.textures!.multiplier);
        sprite.anchor.set(0.5);
        sprite.scale.set(0.8);
        container.addChild(sprite);

        // Multiplier label
        const text = new Text({
          text: `x${obj.value}`,
          style: new TextStyle({ fontSize: 11, fill: 0xffffff, fontWeight: 'bold' })
        });
        text.anchor.set(0.5);
        text.y = 26;
        container.addChild(text);

      } else if (obj.type === 'missile') {
        sprite = new Sprite(this.textures!.missile);
        sprite.anchor.set(0.5);
        sprite.scale.set(0.9);
        container.addChild(sprite);
      } else if (obj.type === 'fuel_canister') {
        sprite = new Sprite(this.textures!.fuel_canister);
        sprite.anchor.set(0.5);
        sprite.scale.set(0.7);
        container.addChild(sprite);

        // Fuel label
        const text = new Text({
          text: `+${obj.value}%`,
          style: new TextStyle({ fontSize: 10, fill: 0xffcc00, fontWeight: 'bold' })
        });
        text.anchor.set(0.5);
        text.y = 24;
        container.addChild(text);
      } else if (obj.type === 'turret') {
        sprite = new Sprite(this.textures!.turret);
        sprite.anchor.set(0.5, 1); // Anchor at bottom center
        sprite.scale.set(0.8);
        container.addChild(sprite);
      } else if (obj.type === 'enemy_plane') {
        sprite = new Sprite(this.textures!.enemy_plane);
        sprite.anchor.set(0.5);
        sprite.scale.set(0.7);
        // Flip if flying towards player
        if (obj.direction === -1) {
          sprite.scale.x = -0.7;
        }
        container.addChild(sprite);
      }

      container.x = x;
      container.y = y;
      this.objectsContainer.addChild(container);

      const gameObj: GameObjectSprite = {
        data: obj,
        container,
        collected: false,
        index
      };
      this.gameObjects.push(gameObj);

      // Register enemy planes and turrets for AI updates
      if (obj.type === 'enemy_plane') {
        this.enemyPlanes.push({
          obj: gameObj,
          currentY: y,
          vy: 0,
          lastShootTime: 0,
          nextDirectionChange: Math.random() * 2
        });
      } else if (obj.type === 'turret') {
        this.turrets.push({
          obj: gameObj,
          lastShootTime: 0
        });
      }
    });
  }

  private update(ticker: { deltaTime: number }) {
    const dt = ticker.deltaTime / 60; // Convert to seconds

    if (this.state === 'demo') {
      this.updateDemo(dt);
    } else if (this.state === 'taking_off') {
      this.updateTakeoff(dt);
    } else if (this.state === 'flying') {
      this.updateFlight(dt);
      this.updateBullets(dt);
      this.updateEnemies(dt);
      this.updateEnemyBullets(dt);

      // Multiplayer updates
      if (this.gameMode === 'multiplayer') {
        this.checkPlayerCollisions();
        this.sendPositionUpdate();
        if (this.remotePlayerManager) {
          this.remotePlayerManager.update(dt, this.cameraX);
        }
      }
    } else if (this.state === 'spectating' && this.gameMode === 'multiplayer') {
      // Spectator mode: follow the leader
      this.updateSpectatorCamera();
      this.updateBullets(dt);
      if (this.remotePlayerManager) {
        this.remotePlayerManager.update(dt, this.cameraX);
      }
    }

    // Animate waves
    this.waveOffset += 1;
    this.drawWaves();

    // Move clouds
    this.updateClouds();

    this.updatePlane();
    this.drawCarrier();
    this.updateObjectPositions();
  }

  private updateTakeoff(dt: number) {
    // Accelerate on deck
    this.plane.vx += 200 * dt;
    this.plane.x += this.plane.vx * dt;

    // Start lifting off after passing carrier edge
    if (this.plane.x > this.carrierWidth) {
      // Initial climb to middle of screen
      this.plane.vy = -150;
      this.state = 'flying';
      this.updateShootButtonVisibility();
    }
  }

  private updateDemo(dt: number) {
    this.demoTime += dt;

    // Spawn demo bonuses if none exist
    if (this.gameObjects.length === 0) {
      this.spawnDemoBonuses();
    }

    // Plane flies forward at constant speed
    const speed = 100;
    this.plane.x += speed * dt;

    // Smooth sine wave vertical movement
    const amplitude = this.carrierY * 0.15; // 15% of field height (gentler)
    const frequency = 0.2; // Slower oscillation
    const centerY = this.carrierY * 0.45; // Center of oscillation
    this.plane.y = centerY + Math.sin(this.demoTime * frequency * Math.PI * 2) * amplitude;

    // Update camera to follow
    const targetCameraX = this.plane.x - this.app.screen.width * 0.3;
    this.cameraX = Math.max(0, targetCameraX);

    // Check bonus collisions
    this.checkDemoCollisions();

    // Loop back when too far
    if (this.plane.x > 5000) {
      this.plane.x = 0;
      this.cameraX = 0;
      this.lastCameraX = 0;
      this.demoTime = 0;
      // Respawn bonuses
      this.objectsContainer.removeChildren();
      this.gameObjects = [];
    }
  }

  private spawnDemoBonuses() {
    // Create demo bonuses (no missiles)
    for (let i = 0; i < 30; i++) {
      const x = 400 + i * 150 + Math.random() * 50;
      const y = 50 + Math.random() * (this.carrierY - 100);
      const isMultiplier = Math.random() > 0.7;

      const obj: GameObject = {
        type: isMultiplier ? 'multiplier' : 'bonus',
        x,
        y,
        value: isMultiplier ? 1.5 : Math.floor(5 + Math.random() * 15)
      };

      const container = new Container();
      const sprite = new Sprite(isMultiplier ? this.textures!.multiplier : this.textures!.bonus);
      sprite.anchor.set(0.5);
      sprite.scale.set(0.6);
      container.addChild(sprite);

      const label = new Text({
        text: isMultiplier ? `x${obj.value}` : `+${obj.value}`,
        style: new TextStyle({
          fontSize: 12,
          fill: 0xffffff,
          fontWeight: 'bold'
        })
      });
      label.anchor.set(0.5);
      label.y = 25;
      container.addChild(label);

      container.x = x;
      container.y = y;
      this.objectsContainer.addChild(container);

      this.gameObjects.push({
        data: obj,
        container,
        collected: false,
        index: i
      });
    }
  }

  private checkDemoCollisions() {
    const planeRadius = 25;

    for (const obj of this.gameObjects) {
      if (obj.collected) continue;

      const dx = this.plane.x - obj.data.x;
      const dy = this.plane.y - obj.data.y;
      const dist = Math.sqrt(dx * dx + dy * dy);

      if (dist < planeRadius + 20) {
        obj.collected = true;
        obj.container.visible = false;

        // Screen position for effect
        const screenX = obj.data.x - this.cameraX;
        const screenY = obj.data.y;
        const isMultiplier = obj.data.type === 'multiplier';

        if (isMultiplier) {
          // No sound in demo mode (landing page)
          if (this.state !== 'demo') soundManager.playMultiplier();
          this.showCollectEffect(screenX, screenY, `x${obj.data.value}`, 0x4080ff);
        } else {
          if (this.state !== 'demo') soundManager.playCollect();
          this.showCollectEffect(screenX, screenY, `+${obj.data.value}`, 0x40ff40);
        }
      }
    }
  }

  private flap() {
    // Can't flap without fuel!
    if (this.fuel <= 0) return;

    // Give upward impulse
    this.plane.vy = this.flapStrength;
  }

  private updateFuelUI() {
    const percent = Math.max(0, (this.fuel / this.maxFuel) * 100);
    this.fuelBar.style.width = `${percent}%`;
    this.fuelText.textContent = `${Math.round(percent)}%`;
  }

  private getCurrentLandingPadX(): number {
    // Landing pads at: carrierWidth + landingPadInterval, carrierWidth + 2*landingPadInterval, etc
    return this.carrierWidth + this.currentSegment * this.landingPadInterval;
  }

  private isOverLandingPad(): boolean {
    // In training mode, check ANY landing pad (not just currentSegment)
    if (this.gameMode === 'training') {
      return this.getOverLandingPadSegment() > 0;
    }
    // Solo mode: only check current target pad
    const padX = this.getCurrentLandingPadX();
    return this.plane.x >= padX && this.plane.x <= padX + 150;
  }

  private getOverLandingPadSegment(): number {
    // Check if plane is over any landing pad, return segment number (0 if not over any)
    const padWidth = 150;
    // Calculate which segment we might be over based on X position
    const relativeX = this.plane.x - this.carrierWidth;
    const possibleSegment = Math.floor(relativeX / this.landingPadInterval);

    // Check segments around the calculated one
    for (let seg = Math.max(1, possibleSegment); seg <= possibleSegment + 1; seg++) {
      const padX = this.carrierWidth + seg * this.landingPadInterval;
      if (this.plane.x >= padX && this.plane.x <= padX + padWidth) {
        return seg;
      }
    }
    return 0;
  }

  private updateFlight(dt: number) {
    // Constant forward speed
    this.plane.vx = 150;

    const minY = 50;
    const deckY = this.carrierY; // Landing pad level
    const seaY = this.carrierY + 45; // Sea level (crash into water)

    // Consume fuel only in solo mode (multiplayer has infinite fuel)
    if (this.gameMode === 'solo') {
      this.fuel -= this.fuelConsumption * dt;
      this.updateFuelUI();

      // Out of fuel - engine dies, fall faster
      if (this.fuel <= 0) {
        this.fuel = 0;
        soundManager.stopEngine();
        this.showMessage('⚠️ Нет топлива! Падаем!');
      }
    }

    // Apply gravity (much stronger when out of fuel - solo only)
    const gravityMultiplier = (this.gameMode === 'solo' && this.fuel <= 0) ? 2.5 : 1;
    this.plane.vy += this.gravity * gravityMultiplier * dt;

    // Clamp vertical speed
    this.plane.vy = Math.max(this.maxRiseSpeed, Math.min(this.maxFallSpeed, this.plane.vy));

    // Update position
    this.plane.x += this.plane.vx * dt;
    this.plane.y += this.plane.vy * dt;

    // Keep in vertical bounds
    this.plane.y = Math.max(minY, this.plane.y);

    // Check if landing on pad (deck level) - solo and training mode
    if ((this.gameMode === 'solo' || this.gameMode === 'training') && this.plane.y >= deckY && this.isOverLandingPad()) {
      this.plane.y = deckY;
      if (this.gameMode === 'training') {
        this.showTrainingLandingDialog();
      } else {
        this.showLandingDialog();
      }
      return;
    }

    // Check if crashed into sea
    if (this.plane.y >= seaY) {
      this.plane.y = seaY;
      this.plane.alive = false;
      soundManager.playExplosion();
      this.finishRound(true);
      return;
    }

    // Multiplayer: check if crossed finish line
    if (this.gameMode === 'multiplayer' && this.plane.x >= this.mpFinishDistance) {
      this.plane.alive = false;
      soundManager.stopEngine();
      this.showMessage('FINISH!');
      this.finishRound(false);
      return;
    }

    // Training: check if crossed finish line (20000m)
    if (this.gameMode === 'training' && this.plane.x >= this.trainingFinishDistance) {
      this.plane.alive = false;
      soundManager.stopEngine();
      this.showMessage('FINISH! 20 km!');
      this.finishRound(false);
      return;
    }

    // Training: check for missed landing pads
    if (this.gameMode === 'training') {
      const padWidth = 150;

      // Check each pad we haven't checked yet
      // Pads are at: carrierWidth + seg * landingPadInterval, starting from seg=1
      const nextPadToCheck = this.lastCheckedPadSegment + 1;
      const nextPadEndX = this.carrierWidth + nextPadToCheck * this.landingPadInterval + padWidth;

      // If we've flown past this pad's end without landing on it
      if (this.plane.x > nextPadEndX) {
        this.missedLandings++;
        this.lastCheckedPadSegment = nextPadToCheck;
        this.showMessage(`⚠️ Пропущена посадка #${nextPadToCheck}! (всего: ${this.missedLandings})`);
      }
    }

    // Update camera (follow plane)
    const targetCameraX = this.plane.x - this.app.screen.width * 0.3;
    this.cameraX = Math.max(0, targetCameraX);

    // Update game values
    this.currentDistance = this.plane.x;

    // Check collisions with objects
    this.checkCollisions();

    this.updateUI();
  }

  private updateSpectatorCamera(): void {
    // Find the furthest alive remote player and follow them
    if (!this.remotePlayerManager) return;

    const alivePlayers = this.remotePlayerManager.getAllPlayers().filter(p => p.alive);

    if (alivePlayers.length === 0) {
      // No one alive - game will end soon
      return;
    }

    // Find the leader (furthest X position)
    let leaderX = 0;
    for (const player of alivePlayers) {
      const playerX = player.getX();
      if (playerX > leaderX) {
        leaderX = playerX;
      }
    }

    // Follow the leader with camera
    const targetCameraX = leaderX - this.app.screen.width * 0.3;
    this.cameraX = Math.max(0, targetCameraX);

    // Update HUD
    this.updateMpHud();
  }

  private checkPlayerCollisions(): void {
    if (!this.remotePlayerManager || !this.plane.alive) return;

    const pushRadius = 60;  // Distance at which push starts
    const pushStrength = 150;  // Push force

    for (const player of this.remotePlayerManager.getAllPlayers()) {
      if (!player.alive) continue;

      // Get remote player position (world coordinates)
      const remoteX = player.getX();
      const remoteY = player.getY();

      // Calculate distance
      const dx = this.plane.x - remoteX;
      const dy = this.plane.y - remoteY;
      const dist = Math.sqrt(dx * dx + dy * dy);

      // If too close, apply push force
      if (dist < pushRadius && dist > 0) {
        // Normalize direction and apply push
        const pushFactor = (pushRadius - dist) / pushRadius;
        const pushX = (dx / dist) * pushStrength * pushFactor;
        const pushY = (dy / dist) * pushStrength * pushFactor;

        // Apply push to velocity (only Y affects movement significantly)
        this.plane.vy += pushY * 0.1;

        // Slight X push (slower effect)
        this.plane.x += pushX * 0.02;
      }
    }
  }

  private showLandingDialog() {
    this.state = 'finished'; // Pause the game
    soundManager.stopEngine();
    this.flapBtn.classList.remove('visible');
    this.updateShootButtonVisibility(false);
    this.refueledOnPad = false; // Reset refuel flag for this pad

    // Calculate current winnings: (distance * rate + bonuses) * multiplier
    const distanceEarnings = this.currentDistance * this.DISTANCE_RATE;
    const baseEarnings = distanceEarnings + this.collectedBonuses;
    const potentialWinnings = baseEarnings * this.currentMultiplier;

    // Update dialog info
    document.getElementById('dialog-collected')!.textContent = `${potentialWinnings.toFixed(2)} FLY (×${this.currentMultiplier.toFixed(1)})`;
    document.getElementById('dialog-fuel')!.textContent = `${Math.round(this.fuel)}%`;
    document.getElementById('dialog-segment')!.textContent = `${this.currentSegment}`;

    // Check if can afford refuel
    const refuelBtn = document.getElementById('btn-refuel') as HTMLButtonElement;
    const canRefuel = this.balance >= this.refuelCost && this.fuel < this.maxFuel;
    refuelBtn.disabled = !canRefuel;
    refuelBtn.textContent = `⛽ +${this.refuelAmount}% (${this.refuelCost} FLY)`;

    this.landingDialog.classList.add('visible');
  }

  private hideLandingDialog() {
    this.landingDialog.classList.remove('visible');
  }

  private showTrainingLandingDialog() {
    this.state = 'finished'; // Pause the game
    soundManager.stopEngine();
    this.flapBtn.classList.remove('visible');
    this.updateShootButtonVisibility(false);

    // Calculate which pad we landed on
    const landedOnPad = this.getOverLandingPadSegment();

    // Mark this pad as checked (landed on, not missed)
    if (landedOnPad > this.lastCheckedPadSegment) {
      this.lastCheckedPadSegment = landedOnPad;
    }

    // Set currentSegment to the pad we landed on (for proper counting)
    this.currentSegment = landedOnPad;

    // Increment successful landings only if first time landing on this pad
    if (!this.landedPads.has(landedOnPad)) {
      this.landedPads.add(landedOnPad);
      this.successfulLandings++;
    }

    // Calculate potential winnings (not credited)
    const distanceEarnings = this.currentDistance * this.DISTANCE_RATE;
    const baseEarnings = distanceEarnings + this.collectedBonuses;
    const potentialWinnings = baseEarnings * this.currentMultiplier;

    // Update dialog info
    document.getElementById('dialog-collected')!.textContent = `${potentialWinnings.toFixed(2)} FLY (не зачислено)`;
    document.getElementById('dialog-fuel')!.textContent = `∞`;
    document.getElementById('dialog-segment')!.textContent = `✅ ${this.successfulLandings} посадок, ❌ ${this.missedLandings} пропущено`;

    // Hide refuel and cashout buttons for training
    const refuelBtn = document.getElementById('btn-refuel') as HTMLButtonElement;
    const cashoutBtn = document.getElementById('btn-cashout') as HTMLButtonElement;
    const continueBtn = document.getElementById('btn-continue') as HTMLButtonElement;

    refuelBtn.style.display = 'none';
    cashoutBtn.textContent = '🏁 Завершить тренировку';
    continueBtn.textContent = '✈️ Лететь дальше';

    this.landingDialog.classList.add('visible');
  }

  private hideTrainingLandingDialog() {
    this.landingDialog.classList.remove('visible');

    // Restore buttons for next time
    const refuelBtn = document.getElementById('btn-refuel') as HTMLButtonElement;
    const cashoutBtn = document.getElementById('btn-cashout') as HTMLButtonElement;
    const continueBtn = document.getElementById('btn-continue') as HTMLButtonElement;

    refuelBtn.style.display = '';
    cashoutBtn.textContent = '💰 Забрать заработок';
    continueBtn.textContent = '✈️ Лететь дальше';
  }

  private async onRefuel() {
    if (this.balance < this.refuelCost) return;

    const refuelBtn = document.getElementById('btn-refuel') as HTMLButtonElement;
    refuelBtn.disabled = true;

    try {
      // Send fuel amount, server calculates cost
      const result = await apiClient.refuel(this.refuelAmount);
      this.balance = result.new_balance;
      this.fuel = Math.min(this.maxFuel, this.fuel + this.refuelAmount);
      this.refueledOnPad = true;
      this.updateFuelUI();
      this.updateUI();

      // Update dialog
      document.getElementById('dialog-fuel')!.textContent = `${Math.round(this.fuel)}%`;
      refuelBtn.disabled = this.balance < this.refuelCost || this.fuel >= this.maxFuel;
    } catch (e) {
      refuelBtn.disabled = false;
    }
  }

  private onCashout() {
    if (this.gameMode === 'training') {
      this.hideTrainingLandingDialog();
      this.finishRound(false);
      return;
    }

    if (this.refueledOnPad) {
      const confirmed = confirm('Вы заправились, но топливо пропадёт если заберёте заработок сейчас. Продолжить?');
      if (!confirmed) return;
    }
    this.hideLandingDialog();
    this.finishRound(false);
  }

  private onContinueFlight() {
    if (this.gameMode === 'training') {
      this.hideTrainingLandingDialog();
    } else {
      this.hideLandingDialog();
    }

    // Move to next segment
    this.currentSegment++;

    // Resume flying
    this.state = 'flying';
    this.plane.vy = -100; // Small boost to take off

    soundManager.startEngine();
    this.flapBtn.classList.add('visible');
    this.updateShootButtonVisibility();
    this.showMessage(`Сегмент ${this.currentSegment}! Лети к следующей площадке!`);
  }

  private checkCollisions() {
    const planeScreenX = this.plane.x - this.cameraX;
    const planeScreenY = this.plane.y;

    for (const obj of this.gameObjects) {
      if (obj.collected) continue;

      // obj.container.x/y are already screen coordinates (camera offset applied in updateObjectPositions)
      const objScreenX = obj.container.x;
      const objScreenY = obj.container.y;

      // Skip objects that are far off-screen (optimization + safety)
      if (objScreenX < -100 || objScreenX > this.app.screen.width + 100) continue;

      const dx = planeScreenX - objScreenX;
      const dy = planeScreenY - objScreenY;
      const dist = Math.sqrt(dx * dx + dy * dy);

      // Different hitboxes for different objects
      let collisionDist = 35;
      if (obj.data.type === 'missile') collisionDist = 25;
      else if (obj.data.type === 'enemy_plane') collisionDist = 30;
      else if (obj.data.type === 'turret') continue; // Skip turrets - no direct collision

      if (dist < collisionDist) {
        obj.collected = true;

        if (obj.data.type === 'bonus') {
          this.collectedIndices.push(obj.index);
          this.collectedBonuses += obj.data.value;
          this.showCollectEffect(objScreenX, objScreenY, `+${obj.data.value}`, 0x40ff40);
          obj.container.visible = false;
          soundManager.playCollect();

          // Notify server in multiplayer
          if (this.gameMode === 'multiplayer') {
            wsClient.collectBonus(obj.index);
          }
        } else if (obj.data.type === 'multiplier') {
          this.collectedIndices.push(obj.index);
          this.currentMultiplier *= obj.data.value;
          this.showCollectEffect(objScreenX, objScreenY, `x${obj.data.value}`, 0x4080ff);
          obj.container.visible = false;
          soundManager.playMultiplier();

          // Notify server in multiplayer
          if (this.gameMode === 'multiplayer') {
            wsClient.collectBonus(obj.index);
          }
        } else if (obj.data.type === 'missile') {
          // Check armor first
          if (this.handleArmorHit()) {
            // Armor absorbed the hit - destroy missile
            obj.collected = true;
            obj.container.visible = false;
            soundManager.playHit();
          } else {
            // No armor - crash!
            this.plane.alive = false;
            this.showCollectEffect(planeScreenX, planeScreenY, 'BOOM!', 0xff4040);
            soundManager.playExplosion();
            this.finishRound(true);
            return;
          }
        } else if (obj.data.type === 'fuel_canister') {
          // Collect fuel
          obj.collected = true;
          this.fuel = Math.min(this.maxFuel, this.fuel + obj.data.value);
          this.updateFuelUI();
          this.showCollectEffect(objScreenX, objScreenY, `+${obj.data.value}%`, 0xffaa00);
          obj.container.visible = false;
          soundManager.playCollect();
        } else if (obj.data.type === 'enemy_plane') {
          // Collision with enemy plane - both take damage
          if (this.handleArmorHit()) {
            obj.collected = true;
            obj.container.visible = false;
            this.showCollectEffect(objScreenX, objScreenY, 'CRASH!', 0xff4040);
            soundManager.playRocketExplosion();
          } else {
            this.plane.alive = false;
            this.showCollectEffect(planeScreenX, planeScreenY, 'BOOM!', 0xff4040);
            soundManager.playExplosion();
            this.finishRound(true);
            return;
          }
        }
        // turret - no collision, player can't crash into them
      }
    }
  }

  private showCollectEffect(x: number, y: number, text: string, color: number) {
    const effect = new Text({
      text,
      style: new TextStyle({
        fontSize: 24,
        fill: color,
        fontWeight: 'bold',
        dropShadow: {
          color: 0x000000,
          blur: 4,
          distance: 2
        }
      })
    });
    effect.anchor.set(0.5);
    effect.x = x;
    effect.y = y;
    this.effectsContainer.addChild(effect);

    // Animate and remove
    let frame = 0;
    const animate = () => {
      frame++;
      effect.y -= 2;
      effect.alpha = 1 - frame / 30;
      if (frame < 30) {
        requestAnimationFrame(animate);
      } else {
        this.effectsContainer.removeChild(effect);
      }
    };
    animate();
  }

  private updateObjectPositions() {
    // Move objects relative to camera (world coordinates - camera offset)
    for (const obj of this.gameObjects) {
      obj.container.x = obj.data.x - this.cameraX;
    }
  }

  private async finishRound(crashed: boolean) {
    this.flapBtn.classList.remove('visible');
    this.statsTable.classList.remove('visible');
    this.gunRow.classList.remove('visible');
    this.updateShootButtonVisibility(false);

    // Stop engine (explosion already stops it, but this handles successful finish)
    if (!crashed) {
      soundManager.stopEngine();
    }

    // Multiplayer: notify server and enter spectator mode
    if (this.gameMode === 'multiplayer') {
      this.state = 'spectating'; // Continue observing other players
      const reason = crashed ? 'crashed' : 'landed';
      wsClient.playerFinished(reason, this.currentDistance);
      this.updateMpHud();
      this.showMessage(crashed ? 'Крушение! Наблюдаем за другими...' : 'Финиш! Наблюдаем за другими...');
      // game_end will trigger showMpResult via WebSocket handler
      return;
    }

    // Training mode: no server communication, just show result
    if (this.gameMode === 'training') {
      this.state = 'finished';
      await this.showTrainingResultDialog(crashed);
      return;
    }

    // Solo mode
    this.state = 'finished';

    // Solo mode: normal flow
    let payout = 0;
    try {
      // Calculate spent ammo, armor, and boost
      const ammoSpent = this.initialAmmo - this.tuning.ammo;
      const armorUsed = this.initialArmor - this.tuning.armor;
      const boostUsed = this.initialBoost - this.tuning.boost;
      // Note: boostUsed is calculated but not sent to server yet (needs API update)
      void boostUsed; // Suppress unused warning

      const result = await apiClient.completeRound(
        this.roundData!.id,
        this.collectedIndices,
        this.currentDistance,
        crashed,
        ammoSpent,
        armorUsed
      );
      this.balance = result.new_balance;
      payout = result.payout;
    } catch (e) {
      this.showMessage('Ошибка завершения раунда');
      return;
    }

    this.updateUI();

    // Show result dialog
    await this.showResultDialog(crashed, payout);
  }

  private async showResultDialog(crashed: boolean, payout: number) {
    const dialog = document.getElementById('result-dialog')!;
    const title = document.getElementById('result-title')!;
    const distanceEl = document.getElementById('result-distance')!;
    const earningsEl = document.getElementById('result-earnings')!;
    const earningsRow = document.getElementById('result-earnings-row')!;
    const rankEl = document.getElementById('result-rank')!;
    const topDistanceEl = document.getElementById('top-distance')!;
    const topBalanceEl = document.getElementById('top-balance')!;

    // Set title
    if (crashed) {
      title.textContent = '💥 Крушение!';
      title.className = 'crashed';
      earningsRow.style.display = 'none';
    } else {
      title.textContent = '✅ Успешный полёт!';
      title.className = 'success';
      earningsRow.style.display = 'flex';
      earningsEl.textContent = `+${payout.toFixed(2)} FLY`;
    }

    // Set distance
    distanceEl.textContent = `${Math.round(this.currentDistance)} м`;

    // Load leaderboard
    try {
      const leaderboard = await apiClient.getLeaderboard();

      // Populate top distance
      topDistanceEl.innerHTML = leaderboard.top_distance
        .map(p => `<li>${p.nickname}: ${Math.round(p.value)} м</li>`)
        .join('');

      // Populate top balance
      topBalanceEl.innerHTML = leaderboard.top_balance
        .map(p => `<li>${p.nickname}: ${p.value.toFixed(0)} FLY</li>`)
        .join('');

      // Set rank
      if (leaderboard.user_rank_distance) {
        rankEl.textContent = `#${leaderboard.user_rank_distance} по дистанции`;
      } else {
        rankEl.textContent = '-';
      }
    } catch (e) {
      topDistanceEl.innerHTML = '<li>Ошибка загрузки</li>';
      topBalanceEl.innerHTML = '<li>Ошибка загрузки</li>';
      rankEl.textContent = '-';
    }

    dialog.classList.add('visible');

    // Wait for button click
    return new Promise<void>(resolve => {
      const againBtn = document.getElementById('result-again')!;
      const menuBtn = document.getElementById('result-menu')!;

      const cleanup = () => {
        againBtn.removeEventListener('click', againHandler);
        menuBtn.removeEventListener('click', menuHandler);
        dialog.classList.remove('visible');
      };

      const againHandler = () => {
        cleanup();
        this.resetToIdle();
        // Start new game immediately
        this.startSoloGame();
        resolve();
      };

      const menuHandler = () => {
        cleanup();
        this.resetToIdle();
        this.menuController.updateBalance(this.balance);
        this.menuController.showMainMenu();
        resolve();
      };

      againBtn.addEventListener('click', againHandler);
      menuBtn.addEventListener('click', menuHandler);
    });
  }

  private async showTrainingResultDialog(crashed: boolean): Promise<void> {
    const dialog = document.getElementById('result-dialog')!;
    const title = document.getElementById('result-title')!;
    const distanceEl = document.getElementById('result-distance')!;
    const earningsEl = document.getElementById('result-earnings')!;
    const earningsRow = document.getElementById('result-earnings-row')!;
    const earningsLabel = earningsRow.querySelector('.result-label') as HTMLElement;
    const rankEl = document.getElementById('result-rank')!;
    const leaderboardSection = document.querySelector('.leaderboard-section') as HTMLElement;
    const againBtn = document.getElementById('result-again')!;
    const menuBtn = document.getElementById('result-menu')!;

    // Set title based on result
    if (crashed) {
      title.textContent = '💥 Крушение!';
      title.className = 'crashed';
    } else {
      title.textContent = '🎯 Тренировка завершена!';
      title.className = 'success';
    }

    // Set distance
    distanceEl.textContent = `${Math.round(this.currentDistance)} м`;

    // Calculate and show potential earnings (not credited)
    const distanceEarnings = this.currentDistance * this.DISTANCE_RATE;
    const baseEarnings = distanceEarnings + this.collectedBonuses;
    const potentialWinnings = baseEarnings * this.currentMultiplier;

    earningsRow.style.display = 'flex';
    earningsLabel.textContent = 'Было бы';
    earningsEl.textContent = `+${potentialWinnings.toFixed(2)} FLY (не зачислено)`;

    // Show missed landings instead of rank
    const rankRow = rankEl.parentElement!;
    const rankLabel = rankRow.querySelector('.result-label') as HTMLElement;
    rankLabel.textContent = 'Посадки';
    rankEl.textContent = `✅ ${this.successfulLandings} / ❌ ${this.missedLandings}`;

    // Hide leaderboard for training
    leaderboardSection.style.display = 'none';

    dialog.classList.add('visible');

    return new Promise<void>(resolve => {
      const cleanup = () => {
        againBtn.removeEventListener('click', againHandler);
        menuBtn.removeEventListener('click', menuHandler);
        dialog.classList.remove('visible');
        // Reset dialog state for next time
        earningsLabel.textContent = 'Заработок';
        rankLabel.textContent = 'Ваше место';
        leaderboardSection.style.display = '';
      };

      const againHandler = () => {
        cleanup();
        this.resetToIdle();
        // Start new training game
        this.startTrainingGame();
        resolve();
      };

      const menuHandler = () => {
        cleanup();
        this.resetToIdle();
        this.menuController.updateBalance(this.balance);
        this.menuController.showMainMenu();
        resolve();
      };

      againBtn.addEventListener('click', againHandler);
      menuBtn.addEventListener('click', menuHandler);
    });
  }

  private resetToIdle() {
    this.state = 'idle';
    this.gameMode = 'solo';
    this.plane.x = 80;
    this.plane.y = this.carrierY - 30;
    this.plane.alive = true;
    this.cameraX = 0;
    this.lastCameraX = 0;
    this.currentDistance = 0;
    this.currentMultiplier = 1.0;
    this.collectedIndices = [];
    this.collectedBonuses = 0;
    this.objectsContainer.removeChildren();
    this.gameObjects = [];
    this.enemyPlanes = [];
    this.turrets = [];
    this.clearEnemyBullets();
    this.showMessage('');

    // Cleanup multiplayer
    if (this.remotePlayerManager) {
      this.remotePlayerManager.clear();
    }
    this.hideMpHud();
    this.updateShootButtonVisibility(false);

    // Disconnect WebSocket to prevent stale messages
    wsClient.disconnect();

    // Clear bullets
    this.bulletsContainer.removeChildren();
    this.bullets = [];

    this.updateUI();
    this.updatePlane();
    this.drawCarrier();
  }

  // ========== Multiplayer Methods ==========

  private setupMultiplayerHandlers(): void {
    wsClient.on('positions_sync', (msg) => {
      if (this.remotePlayerManager && this.gameMode === 'multiplayer') {
        // Filter out our own position and convert normalized Y to screen Y
        const minY = 50;
        const otherPositions = msg.players
          .filter((p: any) => p.user_id !== this.myUserId)
          .map((p: any) => ({
            ...p,
            // Convert normalized Y (0-1) back to screen Y
            y: minY + p.y * (this.carrierY - minY)
          }));
        this.remotePlayerManager.updatePositions(otherPositions);
        this.updateMpHud();
      }
    });

    wsClient.on('bonus_collected', (msg) => {
      if (this.gameMode !== 'multiplayer') return;
      // Mark bonus as collected by another player
      const obj = this.gameObjects.find(o => o.index === msg.bonus_index);
      if (obj && !obj.collected && msg.collector_id !== this.myUserId) {
        obj.collected = true;
        obj.container.visible = false;
        // Show effect
        const screenX = obj.data.x - this.cameraX;
        this.showCollectEffect(screenX, obj.container.y, 'Taken!', 0x888888);
      }
    });

    wsClient.on('bonus_already_collected', (msg) => {
      if (this.gameMode !== 'multiplayer') return;
      // Revert our collection
      const obj = this.gameObjects.find(o => o.index === msg.bonus_index);
      if (obj) {
        // Already collected by someone else - we don't get it
      }
    });

    wsClient.on('player_finished', (msg) => {
      if (this.gameMode !== 'multiplayer') return;
      if (this.remotePlayerManager && msg.user_id !== this.myUserId) {
        const player = this.remotePlayerManager.getPlayer(msg.user_id);
        if (player) {
          player.setFinished(msg.reason, msg.distance);
          this.updateMpHud();
        }
      }
    });

    // Handle being hit by another player
    wsClient.on('player_hit', (msg) => {
      // Only process in multiplayer mode
      if (this.gameMode !== 'multiplayer') return;

      // If WE are the target
      if (msg.target_id === this.myUserId) {
        const screenX = this.plane.x - this.cameraX;

        if (msg.result === 'armor_absorbed') {
          // Armor absorbed the hit
          this.tuning.armor = msg.target_armor;
          this.updateTuningUI();
          this.showCollectEffect(screenX, this.plane.y, 'ARMOR!', 0x4080ff);
          soundManager.playHit();
        } else if (msg.result === 'hit') {
          // Hit without armor
          this.showCollectEffect(screenX, this.plane.y, 'HIT!', 0xff4040);
          soundManager.playHit();
        } else if (msg.result === 'killed') {
          // We got shot down!
          this.plane.alive = false;
          this.showCollectEffect(screenX, this.plane.y, 'SHOT DOWN!', 0xff0000);
          soundManager.playExplosion();
          this.finishRound(true);
        }
      }
      // If someone ELSE was hit/killed, update their state
      else if (this.remotePlayerManager) {
        const player = this.remotePlayerManager.getPlayer(msg.target_id);
        if (player) {
          if (msg.result === 'killed') {
            player.setFinished('shot_down', player.getDistance());
            this.updateMpHud();
          }
        }
      }
    });
  }

  private startSoloGame(): void {
    this.gameMode = 'solo';
    soundManager.init(); // Initialize audio context on user interaction
    this.startGame();
  }

  private startTrainingGame(): void {
    this.gameMode = 'training';
    soundManager.init();
    this.startGame();
  }

  private startMultiplayerGame(
    _roomId: number,
    _seed: string,
    objectsMap: any[],
    players: PlayerInfo[],
    startAt: number
  ): void {
    this.gameMode = 'multiplayer';
    this.mpObjectsMap = objectsMap;

    // Dynamic finish distance: 5000m per player
    this.mpFinishDistance = 5000 * players.length;

    // Create remote player manager
    if (this.planeTexture) {
      if (this.remotePlayerManager) {
        this.remotePlayerManager.clear();
      }
      this.remotePlayerManager = new RemotePlayerManager(
        this.app.stage,
        this.planeTexture
      );

      // Add other players
      for (const p of players) {
        if (p.user_id !== this.myUserId) {
          this.remotePlayerManager.addPlayer(p.user_id, p.nickname, p.color);
        }
      }
    }

    // Setup game with server-provided data
    this.setupMultiplayerObjects();

    // Reset position state - everyone starts at same place
    this.plane.x = 0;
    this.plane.y = this.carrierY * 0.5;
    this.plane.vx = 150;
    this.plane.vy = 0;
    this.plane.alive = true;
    this.collectedIndices = [];
    this.currentMultiplier = 1.0;
    this.currentDistance = 0;
    this.cameraX = 0;
    this.lastCameraX = 0;
    this.fuel = this.maxFuel;
    this.currentSegment = 1;
    this.collectedBonuses = 0;

    // Calculate delay until synchronized start time
    const now = Date.now();
    const delay = Math.max(0, startAt - now);

    this.state = 'idle';
    this.lastPositionSendTime = startAt; // Don't send positions until start time

    // Show countdown based on remaining time
    if (delay > 600) {
      this.showMessage('3...');
      setTimeout(() => this.showMessage('2...'), delay - 600);
      setTimeout(() => this.showMessage('1...'), delay - 300);
    } else if (delay > 300) {
      this.showMessage('2...');
      setTimeout(() => this.showMessage('1...'), delay - 300);
    } else if (delay > 0) {
      this.showMessage('1...');
    }

    // Start flying at synchronized time
    setTimeout(() => {
      this.state = 'flying';
      this.plane.x = 80;
      soundManager.startEngine();
      this.flapBtn.classList.add('visible');
      this.updateShootButtonVisibility();
      this.showMpHud();
      this.showMessage('GO!');
    }, delay);
  }

  private setupMultiplayerObjects(): void {
    this.objectsContainer.removeChildren();
    this.gameObjects = [];
    this.enemyPlanes = [];
    this.turrets = [];
    this.clearEnemyBullets();

    if (!this.textures) return;

    const scaleY = (this.carrierY - 50) / this.fieldHeight;

    this.mpObjectsMap.forEach((obj, index) => {
      const container = new Container();
      const x = obj.x;
      // Turrets stand on water level, other objects use normal Y scaling
      const y = obj.type === 'turret'
        ? this.carrierY + 50  // Submerged in water, only top visible
        : 50 + (this.fieldHeight - obj.y) * scaleY;

      let sprite: Sprite;

      if (obj.type === 'bonus') {
        sprite = new Sprite(this.textures!.bonus);
        sprite.anchor.set(0.5);
        sprite.scale.set(0.75);
        container.addChild(sprite);

        const text = new Text({
          text: `+${obj.value.toFixed(0)}`,
          style: new TextStyle({ fontSize: 11, fill: 0xffffff, fontWeight: 'bold' })
        });
        text.anchor.set(0.5);
        text.y = 24;
        container.addChild(text);

      } else if (obj.type === 'multiplier') {
        sprite = new Sprite(this.textures!.multiplier);
        sprite.anchor.set(0.5);
        sprite.scale.set(0.8);
        container.addChild(sprite);

        const text = new Text({
          text: `x${obj.value}`,
          style: new TextStyle({ fontSize: 11, fill: 0xffffff, fontWeight: 'bold' })
        });
        text.anchor.set(0.5);
        text.y = 26;
        container.addChild(text);

      } else if (obj.type === 'missile') {
        sprite = new Sprite(this.textures!.missile);
        sprite.anchor.set(0.5);
        sprite.scale.set(0.9);
        container.addChild(sprite);
      } else if (obj.type === 'fuel_canister') {
        sprite = new Sprite(this.textures!.fuel_canister);
        sprite.anchor.set(0.5);
        sprite.scale.set(0.7);
        container.addChild(sprite);

        const text = new Text({
          text: `+${obj.value}%`,
          style: new TextStyle({ fontSize: 10, fill: 0xffcc00, fontWeight: 'bold' })
        });
        text.anchor.set(0.5);
        text.y = 24;
        container.addChild(text);
      } else if (obj.type === 'turret') {
        sprite = new Sprite(this.textures!.turret);
        sprite.anchor.set(0.5, 1);
        sprite.scale.set(0.8);
        container.addChild(sprite);
      } else if (obj.type === 'enemy_plane') {
        sprite = new Sprite(this.textures!.enemy_plane);
        sprite.anchor.set(0.5);
        sprite.scale.set(0.7);
        if (obj.direction === -1) {
          sprite.scale.x = -0.7;
        }
        container.addChild(sprite);
      }

      container.x = x;
      container.y = y;
      this.objectsContainer.addChild(container);

      const gameObj: GameObjectSprite = {
        data: obj,
        container,
        collected: false,
        index
      };
      this.gameObjects.push(gameObj);

      // Register enemy planes and turrets for AI updates
      if (obj.type === 'enemy_plane') {
        this.enemyPlanes.push({
          obj: gameObj,
          currentY: y,
          vy: 0,
          lastShootTime: 0,
          nextDirectionChange: Math.random() * 2
        });
      } else if (obj.type === 'turret') {
        this.turrets.push({
          obj: gameObj,
          lastShootTime: 0
        });
      }
    });
  }

  private async showLeaderboard(): Promise<void> {
    const dialog = document.getElementById('result-dialog')!;
    const title = document.getElementById('result-title')!;
    const statsContainer = document.querySelector('.result-stats') as HTMLElement;
    const topDistanceEl = document.getElementById('top-distance')!;
    const topBalanceEl = document.getElementById('top-balance')!;
    const againBtn = document.getElementById('result-again')!;
    const menuBtn = document.getElementById('result-menu')!;

    // Hide stats section, show only leaderboard
    title.textContent = '🏆 Таблица рекордов';
    title.className = '';
    statsContainer.style.display = 'none';
    againBtn.style.display = 'none';
    menuBtn.textContent = 'Закрыть';

    // Load leaderboard
    try {
      const leaderboard = await apiClient.getLeaderboard();
      topDistanceEl.innerHTML = leaderboard.top_distance
        .map(p => `<li>${p.nickname}: ${Math.round(p.value)} м</li>`)
        .join('');
      topBalanceEl.innerHTML = leaderboard.top_balance
        .map(p => `<li>${p.nickname}: ${p.value.toFixed(0)} FLY</li>`)
        .join('');
    } catch {
      topDistanceEl.innerHTML = '<li>Ошибка загрузки</li>';
      topBalanceEl.innerHTML = '<li>Ошибка загрузки</li>';
    }

    dialog.classList.add('visible');

    return new Promise<void>(resolve => {
      const handler = () => {
        menuBtn.removeEventListener('click', handler);
        dialog.classList.remove('visible');
        // Reset dialog state for next time
        statsContainer.style.display = '';
        againBtn.style.display = '';
        menuBtn.textContent = 'В меню';
        this.menuController.updateBalance(this.balance);
        this.menuController.showMainMenu();
        resolve();
      };
      menuBtn.addEventListener('click', handler);
    });
  }

  // Multiplayer HUD
  private showMpHud(): void {
    const hud = document.getElementById('mp-hud')!;
    hud.classList.add('visible');
    this.updateMpHud();
  }

  private hideMpHud(): void {
    const hud = document.getElementById('mp-hud')!;
    hud.classList.remove('visible');
  }

  private updateMpHud(): void {
    if (this.gameMode !== 'multiplayer') return;

    const hud = document.getElementById('mp-hud')!;
    hud.innerHTML = '';

    // Collect all player distances
    const playerData: Array<{
      userId: number;
      nickname: string;
      color: string;
      distance: number;
      alive: boolean;
      isMe: boolean;
    }> = [];

    // Add self
    playerData.push({
      userId: this.myUserId,
      nickname: 'You',
      color: this.menuController.getPlaneColor(),
      distance: this.currentDistance,
      alive: this.plane.alive,
      isMe: true
    });

    // Add remote players
    if (this.remotePlayerManager) {
      for (const player of this.remotePlayerManager.getAllPlayers()) {
        playerData.push({
          userId: player.userId,
          nickname: player.nickname,
          color: player.color,
          distance: player.getDistance(),
          alive: player.alive,
          isMe: false
        });
      }
    }

    // Sort by distance descending
    playerData.sort((a, b) => b.distance - a.distance);

    // Create HUD elements
    const colorMap: Record<string, string> = {
      red: '#e04040',
      blue: '#4080e0',
      green: '#40e040',
      yellow: '#e0e040',
      purple: '#a040e0'
    };

    for (const p of playerData) {
      const div = document.createElement('div');
      div.className = `mp-hud-player ${p.alive ? '' : 'crashed'} ${p.isMe ? 'you' : ''}`;
      div.innerHTML = `
        <div class="color" style="background:${colorMap[p.color] || '#888'}"></div>
        <span>${p.nickname}</span>
        <span class="distance">${Math.round(p.distance)}m</span>
      `;
      hud.appendChild(div);
    }
  }

  private sendPositionUpdate(): void {
    if (this.gameMode !== 'multiplayer' || this.state !== 'flying') return;

    const now = Date.now();
    if (now - this.lastPositionSendTime < this.positionSendInterval) return;

    this.lastPositionSendTime = now;

    // Normalize Y to 0-1 range for cross-screen compatibility
    // 0 = top (minY=50), 1 = sea level (carrierY)
    const minY = 50;
    const normalizedY = (this.plane.y - minY) / (this.carrierY - minY);

    wsClient.sendPosition(
      this.plane.x,
      normalizedY,  // Send normalized Y instead of screen Y
      this.plane.vx,
      this.plane.vy,
      this.fuel,
      this.currentDistance
    );
  }

  private handleResize() {
    const rect = this.canvas.getBoundingClientRect();
    const width = Math.max(300, rect.width);
    const height = Math.max(150, rect.height);

    this.app.renderer.resize(width, height);
    this.carrierY = height - Math.min(80, height * 0.15);

    if (this.state === 'idle') {
      this.plane.y = this.carrierY - 30;
    }

    // Redraw static elements
    this.updatePlane();
    this.drawCarrier();
  }

  // ========== Shooting System ==========

  private shoot(): void {
    if (!this.plane.alive) return;
    if (!this.tuning.gun) return;
    // Training mode has infinite ammo
    if (this.gameMode !== 'training' && this.tuning.ammo <= 0) return;

    const now = Date.now();
    const fireRate = this.tuning.gun.fire_rate * 1000; // Convert to ms
    if (now - this.lastShootTime < fireRate) return;

    this.lastShootTime = now;

    // Determine bullets count based on fire mode
    const burstCount = this.tuning.gun.burst_count;
    const bulletsToFire = Math.min(burstCount, this.tuning.ammo);

    // Fire bullets
    for (let i = 0; i < bulletsToFire; i++) {
      setTimeout(() => this.createBullet(), i * 50); // Slight delay between burst bullets
    }

    // Consume ammo (not in training mode)
    if (this.gameMode !== 'training') {
      this.tuning.ammo -= bulletsToFire;
    }
    this.updateTuningUI();

    // Play sound
    soundManager.playShoot();

    // Notify server in multiplayer
    if (this.gameMode === 'multiplayer') {
      const bulletId = `${this.myUserId}_${this.bulletIdCounter}`;
      wsClient.send({
        type: 'shoot',
        bullet_id: bulletId,
        start_x: this.plane.x,
        start_y: this.plane.y,
        direction_x: 1,
        direction_y: 0,
        bullets_count: bulletsToFire
      });
    }
  }

  private useBoost(): void {
    if (!this.plane.alive) return;
    // Training mode has infinite boost
    if (this.gameMode !== 'training' && this.tuning.boost <= 0) return;

    // Apply boost - instant forward dash
    this.plane.x += this.BOOST_DISTANCE;

    // Consume boost (not in training mode)
    if (this.gameMode !== 'training') {
      this.tuning.boost--;
    }
    this.updateTuningUI();

    // Visual effect
    const screenX = this.plane.x - this.cameraX;
    this.showCollectEffect(screenX, this.plane.y, 'BOOST!', 0xff8800);

    // Play sound (reuse multiplier sound for now)
    soundManager.playMultiplier();
  }

  private createBullet(): void {
    const bulletId = `${this.myUserId}_${++this.bulletIdCounter}`;

    // Get plane's visual angle
    const angle = this.planeSprite.rotation;

    // Calculate bullet velocity based on plane's angle
    const vx = Math.cos(angle) * this.BULLET_SPEED;
    const vy = Math.sin(angle) * this.BULLET_SPEED;

    // Start position at plane's nose
    const startX = this.plane.x + Math.cos(angle) * 30;
    const startY = this.plane.y + Math.sin(angle) * 30;

    // Create bullet sprite
    const sprite = new Graphics();
    sprite.circle(0, 0, 4);
    sprite.fill(0xffff00);
    sprite.x = startX - this.cameraX;
    sprite.y = startY;
    this.bulletsContainer.addChild(sprite);

    const bullet: Bullet = {
      id: bulletId,
      x: startX,
      y: startY,
      vx,
      vy,
      sprite,
      shooterId: this.myUserId,
      distance: 0
    };

    this.bullets.push(bullet);
  }

  private updateBullets(dt: number): void {
    const bulletsToRemove: Bullet[] = [];

    for (const bullet of this.bullets) {
      // Update position
      bullet.x += bullet.vx * dt;
      bullet.y += bullet.vy * dt;
      bullet.distance += this.BULLET_SPEED * dt;

      // Update sprite position
      bullet.sprite.x = bullet.x - this.cameraX;
      bullet.sprite.y = bullet.y;

      // Remove if too far
      if (bullet.distance > this.BULLET_MAX_DISTANCE) {
        bulletsToRemove.push(bullet);
        continue;
      }

      // Check collisions with missiles
      if (bullet.shooterId === this.myUserId) {
        this.checkBulletMissileCollisions(bullet, bulletsToRemove);
        this.checkBulletEnemyCollisions(bullet, bulletsToRemove);

        // Check collisions with other players in multiplayer
        if (this.gameMode === 'multiplayer') {
          this.checkBulletPlayerCollisions(bullet, bulletsToRemove);
        }
      }
    }

    // Remove spent bullets
    for (const bullet of bulletsToRemove) {
      this.bulletsContainer.removeChild(bullet.sprite);
      const idx = this.bullets.indexOf(bullet);
      if (idx >= 0) this.bullets.splice(idx, 1);
    }
  }

  private checkBulletMissileCollisions(bullet: Bullet, bulletsToRemove: Bullet[]): void {
    for (const obj of this.gameObjects) {
      if (obj.collected || obj.data.type !== 'missile') continue;

      const dx = bullet.x - obj.data.x;
      const dy = bullet.y - (obj.container.y);
      const dist = Math.sqrt(dx * dx + dy * dy);

      if (dist < 30) {
        // Hit missile
        obj.missileHits = (obj.missileHits || 0) + 1;
        bulletsToRemove.push(bullet);

        // Show hit effect
        const screenX = obj.data.x - this.cameraX;
        this.showCollectEffect(screenX, obj.container.y, 'HIT!', 0xffaa00);
        soundManager.playHit();

        // Destroy missile if enough hits
        if (obj.missileHits >= this.HITS_TO_KILL_MISSILE) {
          obj.collected = true;
          obj.container.visible = false;
          this.showCollectEffect(screenX, obj.container.y, 'DESTROYED!', 0xff4040);
          soundManager.playRocketExplosion();

          // Notify server in multiplayer
          if (this.gameMode === 'multiplayer') {
            wsClient.send({
              type: 'missile_destroyed',
              missile_index: obj.index
            });
          }
        }

        break;
      }
    }
  }

  private checkBulletPlayerCollisions(bullet: Bullet, bulletsToRemove: Bullet[]): void {
    if (!this.remotePlayerManager) return;

    for (const player of this.remotePlayerManager.getAllPlayers()) {
      if (!player.alive) continue;

      const playerX = player.getX();
      const playerY = player.getY();

      const dx = bullet.x - playerX;
      const dy = bullet.y - playerY;
      const dist = Math.sqrt(dx * dx + dy * dy);

      if (dist < 35) {
        // Hit player
        bulletsToRemove.push(bullet);

        const screenX = playerX - this.cameraX;
        this.showCollectEffect(screenX, playerY, 'HIT!', 0xff4040);
        soundManager.playHit();

        // Notify server
        wsClient.send({
          type: 'player_hit',
          target_id: player.userId,
          damage: 1
        });

        break;
      }
    }
  }

  // Check if player's bullets hit enemy planes
  private checkBulletEnemyCollisions(bullet: Bullet, bulletsToRemove: Bullet[]): void {
    for (const enemy of this.enemyPlanes) {
      if (enemy.obj.collected) continue;

      const dx = bullet.x - enemy.obj.data.x;
      const dy = bullet.y - enemy.currentY;
      const dist = Math.sqrt(dx * dx + dy * dy);

      if (dist < 35) {
        enemy.obj.missileHits = (enemy.obj.missileHits || 0) + 1;
        bulletsToRemove.push(bullet);

        const screenX = enemy.obj.data.x - this.cameraX;
        this.showCollectEffect(screenX, enemy.currentY, 'HIT!', 0xffaa00);
        soundManager.playHit();

        if (enemy.obj.missileHits >= this.HITS_TO_KILL_MISSILE) {
          enemy.obj.collected = true;
          enemy.obj.container.visible = false;
          this.showCollectEffect(screenX, enemy.currentY, 'DESTROYED!', 0xff4040);
          soundManager.playRocketExplosion();
        }

        break;
      }
    }
  }

  private clearEnemyBullets(): void {
    for (const bullet of this.enemyBullets) {
      this.enemyBulletsContainer.removeChild(bullet.sprite);
    }
    this.enemyBullets = [];
  }

  private updateEnemies(dt: number): void {
    const now = Date.now() / 1000;
    const planeWorldX = this.plane.x;

    // Update enemy planes
    for (const enemy of this.enemyPlanes) {
      if (enemy.obj.collected) continue;

      const enemyData = enemy.obj.data;
      const enemyWorldX = enemyData.x;

      // Only update if enemy is within visible range (plus buffer)
      if (enemyWorldX < this.cameraX - 200 || enemyWorldX > this.cameraX + this.app.screen.width + 500) {
        continue;
      }

      // Random vertical movement
      enemy.nextDirectionChange -= dt;
      if (enemy.nextDirectionChange <= 0) {
        enemy.vy = (Math.random() - 0.5) * 100; // Random vertical speed
        enemy.nextDirectionChange = 1 + Math.random() * 2;
      }

      // Apply vertical movement
      enemy.currentY += enemy.vy * dt;
      enemy.currentY = Math.max(80, Math.min(this.carrierY - 50, enemy.currentY));
      enemy.obj.container.y = enemy.currentY;

      // Shooting
      const fireRate = enemyData.fire_rate || 2;
      if (now - enemy.lastShootTime > fireRate) {
        // Calculate direction to player
        const dx = planeWorldX - enemyWorldX;
        const dy = this.plane.y - enemy.currentY;
        const dist = Math.sqrt(dx * dx + dy * dy);

        if (dist < 600) { // Only shoot if player is close enough
          const vx = (dx / dist) * this.ENEMY_BULLET_SPEED;
          const vy = (dy / dist) * this.ENEMY_BULLET_SPEED;

          this.spawnEnemyBullet(enemyWorldX, enemy.currentY, vx, vy);
          enemy.lastShootTime = now;
        }
      }
    }

    // Update turrets
    for (const turret of this.turrets) {
      if (turret.obj.collected) continue;

      const turretData = turret.obj.data;
      const turretWorldX = turretData.x;

      // Only update if turret is within visible range
      if (turretWorldX < this.cameraX - 100 || turretWorldX > this.cameraX + this.app.screen.width + 300) {
        continue;
      }

      // Shooting
      const fireRate = turretData.fire_rate || 2.5;
      if (now - turret.lastShootTime > fireRate) {
        const angle = (turretData.angle || 60) * Math.PI / 180;
        const vx = Math.cos(angle) * this.TURRET_BULLET_SPEED;
        const vy = -Math.sin(angle) * this.TURRET_BULLET_SPEED; // Negative because up is negative Y

        this.spawnEnemyBullet(turretWorldX, turret.obj.container.y - 20, vx, vy);
        turret.lastShootTime = now;
      }
    }
  }

  private spawnEnemyBullet(x: number, y: number, vx: number, vy: number): void {
    const sprite = new Graphics();
    sprite.circle(0, 0, 4);
    sprite.fill(0xff4444);
    sprite.x = x - this.cameraX;
    sprite.y = y;

    this.enemyBulletsContainer.addChild(sprite);
    this.enemyBullets.push({ x, y, vx, vy, sprite });
  }

  private updateEnemyBullets(dt: number): void {
    const bulletsToRemove: EnemyBullet[] = [];

    for (const bullet of this.enemyBullets) {
      // Update position
      bullet.x += bullet.vx * dt;
      bullet.y += bullet.vy * dt;

      // Update sprite position
      bullet.sprite.x = bullet.x - this.cameraX;
      bullet.sprite.y = bullet.y;

      // Remove if off screen
      if (bullet.x < this.cameraX - 100 ||
          bullet.x > this.cameraX + this.app.screen.width + 100 ||
          bullet.y < 0 ||
          bullet.y > this.carrierY + 50) {
        bulletsToRemove.push(bullet);
        continue;
      }

      // Check collision with player
      const dx = bullet.x - this.plane.x;
      const dy = bullet.y - this.plane.y;
      const dist = Math.sqrt(dx * dx + dy * dy);

      if (dist < 25 && this.plane.alive) {
        bulletsToRemove.push(bullet);

        // Handle hit
        if (this.handleArmorHit()) {
          // Armor absorbed the hit
          const screenX = this.plane.x - this.cameraX;
          this.showCollectEffect(screenX, this.plane.y, 'BLOCKED!', 0x4488ff);
        } else {
          // No armor - crash
          this.plane.alive = false;
          this.finishRound(true);
          return;
        }
      }
    }

    // Remove spent bullets
    for (const bullet of bulletsToRemove) {
      this.enemyBulletsContainer.removeChild(bullet.sprite);
      const idx = this.enemyBullets.indexOf(bullet);
      if (idx >= 0) this.enemyBullets.splice(idx, 1);
    }
  }

  private handleArmorHit(): boolean {
    // Called when our plane is hit (by missile or bullet)

    // Training mode: infinite armor
    if (this.gameMode === 'training') {
      const screenX = this.plane.x - this.cameraX;
      this.showCollectEffect(screenX, this.plane.y, 'TRAINING!', 0x4080ff);
      return true; // Always survive in training
    }

    // Check if player has armor
    if (this.tuning.armor > 0) {
      // Consume 1 armor
      this.tuning.armor--;
      this.updateTuningUI();

      // Show armor absorb effect
      const screenX = this.plane.x - this.cameraX;
      this.showCollectEffect(screenX, this.plane.y, `ARMOR! (${this.tuning.armor} left)`, 0x4080ff);

      // Notify server in multiplayer
      if (this.gameMode === 'multiplayer') {
        wsClient.send({ type: 'armor_hit' });
      }

      return true; // Survived with armor
    }
    return false; // No armor, will crash
  }

  private updateShootButtonVisibility(show?: boolean): void {
    const shootBtn = document.getElementById('shoot-btn') as HTMLButtonElement;
    if (!shootBtn) return;

    // If explicitly set to hide, hide it
    if (show === false) {
      shootBtn.classList.remove('visible');
      return;
    }

    // Show button when flying, disable if no gun
    const isFlying = this.state === 'flying';
    if (isFlying) {
      shootBtn.classList.add('visible');
      // Disable if no gun or no ammo
      const hasGunAndAmmo = this.tuning.gun !== null && this.tuning.ammo > 0;
      shootBtn.disabled = !hasGunAndAmmo;
      shootBtn.style.opacity = hasGunAndAmmo ? '1' : '0.4';
    } else {
      shootBtn.classList.remove('visible');
    }
  }

  private updateTuningUI(): void {
    // Update ammo bar and text
    const ammoBar = document.getElementById('ammo-bar');
    const ammoText = document.getElementById('ammo-text');
    if (ammoBar && ammoText) {
      const maxAmmo = this.tuning.gun?.magazine_size || 50; // Default max for display
      const ammoPercent = this.tuning.gun ? Math.min(100, (this.tuning.ammo / maxAmmo) * 100) : 0;
      ammoBar.style.width = `${ammoPercent}%`;
      ammoText.textContent = this.tuning.gun ? `${this.tuning.ammo}` : '-';
    }

    // Update armor bar and text
    const armorBar = document.getElementById('armor-bar');
    const armorText = document.getElementById('armor-text');
    if (armorBar && armorText) {
      const maxArmor = 10; // Max armor for display purposes
      const armorPercent = Math.min(100, (this.tuning.armor / maxArmor) * 100);
      armorBar.style.width = `${armorPercent}%`;
      armorText.textContent = `${this.tuning.armor}`;
    }

    // Update gun indicator row
    const gunEl = document.getElementById('gun-indicator');
    if (gunEl) {
      if (this.tuning.gun) {
        gunEl.textContent = `🔫 ${this.tuning.gun.name}`;
        this.gunRow.classList.add('visible');
      } else {
        gunEl.textContent = 'Нет пулемёта';
        this.gunRow.classList.remove('visible');
      }
    }
  }

  // Public method for MenuController to refresh tuning
  public async refreshTuning(): Promise<void> {
    await this.loadTuning();
    this.updateTuningUI();
  }

  public getTuning(): TuningState {
    return this.tuning;
  }
}
